// The playmat, painted rather than computed.
//
// The zones are the official mat's: Champion and Legend left of a wide Base
// with the Main Deck at the right edge, and below them the Rune Deck, a wide
// Runes strip and the Trash, with the battlefields shared across the middle.
//
// Drawn into a canvas and uploaded as one texture, instead of being rebuilt as
// signed-distance rectangles in WGSL. Seven labelled zones a side is a
// page-layout problem, not a shading one, and a 2D context is the tool that
// already solves it -- including the text, which is the part WGSL cannot do at
// all.
//
// The rectangles come from `ZONES` in layout.ts, the same table the card slots
// are placed from, so a zone and the cards that belong in it cannot drift.

import { BATTLEFIELD_ZONE, MAT, ZONES, type Zone } from './layout.ts';
import { MipmapGenerator, textureFromBitmap } from '../gfx/texture.ts';

/**
 * Pixels per world unit. The mat is ~7.2 x 5.4 units, so this puts it a little
 * over 2K across -- enough that the hairline zone rules stay hairlines when the
 * camera leans in, without paying for a 4K upload.
 */
const PPU = 340;

/**
 * Label height in world units. Set in world terms rather than as a fraction of
 * the zone, so a one-card slot and a whole row are captioned in the same size
 * -- which is what the printed mat does.
 */
/**
 * How tall a zone's label is, in world units.
 *
 * At 0.075 the words came out around ten pixels on screen -- the mat texture is
 * ~2450px wide and is drawn at about 990, so the label is scaled to two fifths
 * of the size it is painted at. Ten pixels of letter-spaced serif on a dark
 * felt is not text anyone reads; it is texture.
 */
const LABEL_H = 0.115;

// Read from ~17 world units away and mipped down hard, so the rules are drawn
// heavier than a printed mat's would be: a hairline at this distance averages
// into the felt and the zones stop reading at all.
const INK = '#eef2fa';
const RULE = 'rgba(226, 234, 248, 0.92)';
// The felt is a *surface*, not the backdrop behind it.
//
// It was painted near-black, which under a tone curve that crushes everything
// below middle grey left the mat as a void with a few hairlines floating in
// it: the zones did not read, and the outer ones were gone entirely. These
// values still sit far below middle grey -- the mat is dark -- but far enough
// above the background that the table has a top.
const FELT_DARK = '#252b3a';
const FELT_LIGHT = '#333b4e';

export function paintPlaymat(
  device: GPUDevice, mipmaps: MipmapGenerator,
  battlefieldZ: number, battlefieldSpacing: number, battlefieldCount: number,
): Promise<GPUTexture> {
  const w = Math.round(MAT.halfWidth * 2 * PPU);
  const h = Math.round(MAT.halfDepth * 2 * PPU);

  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const c = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
  if (!c) throw new Error('playmat: no 2d context');

  // World -> pixels. World +Z is toward the player, which is down the image.
  const px = (x: number) => (x + MAT.halfWidth) * PPU;
  const py = (z: number) => (z + MAT.halfDepth) * PPU;

  felt(c, w, h);

  // Battlefields: shared, across the middle, and landscape like the cards that
  // sit in them.
  const labels: Label[] = [];
  for (let i = 0; i < battlefieldCount; i++) {
    const x = (i - (battlefieldCount - 1) / 2) * battlefieldSpacing;
    labels.push(zoneBox(
      c, px(x), py(battlefieldZ),
      BATTLEFIELD_ZONE.halfW * 2 * PPU, BATTLEFIELD_ZONE.halfD * 2 * PPU,
      'BATTLEFIELD',
    ));
  }

  // Both halves, from one table: the far half is the near half mirrored.
  for (const mine of [true, false]) {
    for (const key of Object.keys(ZONES) as (keyof typeof ZONES)[]) {
      const z: Zone = ZONES[key];
      const cx = mine ? z.x : -z.x;
      const cz = mine ? z.z : -z.z;
      labels.push(zoneBox(
        c, px(cx), py(cz), z.halfW * 2 * PPU, z.halfD * 2 * PPU, z.label,
        // A mirrored label would be upside down to its own player and right way
        // up to nobody, so the far half's labels are turned with the half.
        !mine,
      ));
    }
  }

  // Every name last, so nothing can be drawn across one.
  for (const l of labels) drawLabel(c, l);

  return createImageBitmap(canvas as ImageBitmapSource).then((bitmap) => {
    const tex = textureFromBitmap(device, bitmap, mipmaps, { label: 'playmat' });
    bitmap.close();
    return tex;
  });
}

/** The felt: near-black, with a faint weave so it is not a flat field. */
function felt(c: OffscreenCanvasRenderingContext2D, w: number, h: number): void {
  const g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.62);
  g.addColorStop(0, FELT_LIGHT);
  g.addColorStop(1, FELT_DARK);
  c.fillStyle = g;
  c.fillRect(0, 0, w, h);

  c.globalAlpha = 0.05;
  c.strokeStyle = '#8fa4c8';
  c.lineWidth = 1;
  for (let x = 0; x < w; x += 7) {
    c.beginPath(); c.moveTo(x, 0); c.lineTo(x, h); c.stroke();
  }
  for (let y = 0; y < h; y += 7) {
    c.beginPath(); c.moveTo(0, y); c.lineTo(w, y); c.stroke();
  }
  c.globalAlpha = 1;

  // The centre line, where the two halves meet.
  c.strokeStyle = 'rgba(226, 234, 248, 0.30)';
  c.lineWidth = 5;
  c.beginPath(); c.moveTo(0, h / 2); c.lineTo(w, h / 2); c.stroke();
}

/**
 * One zone: a bracketed rectangle with its name set into the bottom rule.
 *
 * The corner brackets rather than a closed box are what the printed mat uses,
 * and they read better under cards: a full outline shows through the gaps
 * between overlapping cards as a competing edge.
 */
interface Label { cx: number; cy: number; text: string; size: number; flip: boolean }

/**
 * Draws one zone and returns where its name belongs.
 *
 * The name is *not* drawn here. Zones are painted one after another, so a zone
 * drawn later puts its corner brackets straight through the label of the one
 * before it -- which is what made CHAMPION and LEGEND unreadable, with a
 * bracket from the row below crossing every letter. Every box is painted
 * first, then every label on top of all of them.
 */
function zoneBox(
  c: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, w: number, h: number, label: string, flip = false,
): Label {
  const x = cx - w / 2;
  const y = cy - h / 2;
  const arm = Math.min(w, h) * 0.24;
  const inset = Math.min(w, h) * 0.07;

  c.strokeStyle = RULE;
  c.lineWidth = 6;
  c.lineJoin = 'miter';

  // Four corner brackets.
  for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
    const px = sx > 0 ? x : x + w;
    const py = sy > 0 ? y : y + h;
    c.beginPath();
    c.moveTo(px + sx * arm, py);
    c.lineTo(px + sx * inset, py);
    c.lineTo(px, py + sy * inset);
    c.lineTo(px, py + sy * arm);
    c.stroke();
  }

  // The bottom rule, broken where the label sits.
  //
  // A label is set as large as it can be *and still fit the zone it names*.
  // Champion and Legend are narrow boxes side by side, and at one fixed size
  // their names ran past their own brackets and collided into "CHAMPIONLEGEND".
  c.letterSpacing = '2px';
  let size = LABEL_H * PPU;
  c.font = `600 ${Math.round(size)}px "Beaufort", Georgia, serif`;
  const room = w - inset * 2 - arm * 2;
  const natural = c.measureText(label).width;
  if (natural > room) {
    size = Math.max(size * 0.55, size * (room / natural));
    c.font = `600 ${Math.round(size)}px "Beaufort", Georgia, serif`;
  }
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  const text = label;
  const tw = c.measureText(text).width;
  const gap = tw / 2 + size * 0.5;
  const ry = flip ? y : y + h;

  c.beginPath();
  c.moveTo(x + inset + arm, ry);
  c.lineTo(cx - gap, ry);
  c.moveTo(cx + gap, ry);
  c.lineTo(x + w - inset - arm, ry);
  c.stroke();

  return { cx, cy: ry, text, size, flip };
}

/**
 * Sets a zone's name into its rule, over a patch of the felt beneath it.
 *
 * The patch is what makes it readable: the rule is broken for the text, but
 * the brackets of whatever zone sits beyond it are not, and a hairline through
 * the middle of a word is enough to lose it. Painting the felt back over that
 * strip -- the same colour, so nothing shows but the missing line -- leaves the
 * word sitting in a gap in the ink, which is how a printed mat does it.
 */
function drawLabel(c: OffscreenCanvasRenderingContext2D, l: Label): void {
  c.save();
  c.translate(l.cx, l.cy);
  if (l.flip) c.rotate(Math.PI);

  c.font = `600 ${Math.round(l.size)}px "Beaufort", Georgia, serif`;
  c.letterSpacing = '2px';
  c.textAlign = 'center';
  c.textBaseline = 'middle';

  const pad = l.size * 0.42;
  const tw = c.measureText(l.text).width;
  c.fillStyle = FELT_DARK;
  c.fillRect(-tw / 2 - pad, -l.size * 0.72, tw + pad * 2, l.size * 1.44);

  c.fillStyle = INK;
  c.fillText(l.text, 0, 0);
  c.restore();
}
